/**
 * harfbuzz shaping 包装（M2-17 / DESIGN.md §2.5）。
 *
 * 尖刺三（M0-11）验证过的管线，这里收敛成引擎接口：字体字节进、
 * glyph id + advance 出，单位统一为**字体单位**（配合 upem 换算像素）。
 *
 * 已知简化（后续任务）：整段文本一次 shaping，依赖 harfbuzz 的
 * guessSegmentProperties 推断主导 script；真正的多语种混排要做
 * BiDi + script itemization 后逐段 shaping。中英混排在思源黑体这类
 * 同时覆盖两种文字的字体下工作正常——这正是内置它的原因之一。
 */
import { Blob as HbBlob, Buffer as HbBuffer, Direction, Face, Font, shape } from 'harfbuzzjs';

export interface ShapedGlyph {
  /** 字体内 glyph 索引（shaping 之后不再是 Unicode 码点）。 */
  readonly glyphId: number;
  /** 源文本字符簇下标。 */
  readonly cluster: number;
  /** 推进量，字体单位。横排下有意义。 */
  readonly xAdvance: number;
  /** 竖排下的推进量（字体单位，向下为正）。横排时恒为 0。 */
  readonly yAdvance: number;
  readonly xOffset: number;
  readonly yOffset: number;
}

export interface ShapeRun {
  readonly glyphs: readonly ShapedGlyph[];
  /** 字体单位每 em。像素换算：px = 单位 × fontSize / upem。 */
  readonly upem: number;
}

/**
 * `layout.ts`/`vertical.ts`/`raster.ts` 实际依赖的最小接口——只认这几个
 * 方法，不关心背后是单一字体（`TextShaper`）还是带了回退候选的组合体
 * （`engine/text/fallback.ts` 的 `FallbackTextShaper`）。两者都实现这个
 * 接口，排版/栅格化代码完全不用区分，也不用改一行。
 */
export interface Shaper {
  readonly upem: number;
  shape(text: string, vertical?: boolean): ShapeRun;
  glyphPath(glyphId: number): string;
  destroy(): void;
}

/** 与尖刺一致：把字体缩放设为 upem，使输出单位就是字体单位。 */
export class TextShaper implements Shaper {
  readonly #font: Font;
  readonly #face: Face;
  upem: number;

  constructor(fontBytes: Uint8Array) {
    this.#face = new Face(new HbBlob(fontBytes), 0);
    this.#font = new Font(this.#face);
    this.upem = this.#face.upem;
    this.#font.setScale(this.upem, this.upem);
  }

  /** 这款字体是否有这个码点的字形（不含变体选择符）——字体回退按这个判断要不要换一款候选。 */
  hasGlyph(codePoint: number): boolean {
    return this.#font.nominalGlyph(codePoint) !== undefined;
  }

  /**
   * 重设这个字体对象的缩放基准（HarfBuzz 的 font-space 度量单位），
   * 让后续 `shape()`/`glyphPath()` 的输出换算到 `targetUpem` 空间，
   * 不再是这款字体自己的原生 upem——字体回退需要把候选字体的输出
   * 统一换算到主字体的 upem，才能跟主字体的 glyph 共用外层同一个
   * `fontSize/upem` 缩放因子绘制，否则大小、位置全部对不上。
   *
   * ⚠️ 只能对**这次回退专用、没有进 `FontLibrary` 共享缓存**的独立实例
   * 调用——调了会永久改变这个对象自己的输出单位，共享缓存的 shaper
   * （被当作某个族名自己的主字体使用）绝不能调这个，否则会连累其它
   * 同时把它当主字体用的地方全部跑偏。
   */
  rescaleTo(targetUpem: number): void {
    this.upem = targetUpem;
    this.#font.setScale(targetUpem, targetUpem);
  }

  /**
   * @param vertical 竖排（DESIGN.md §2.5 的 vert/vrt2 特性）。
   *   HarfBuzz 在方向为 TTB 时自动请求 vert/vrt2（字体有对应字形才会生效，
   *   没有的字符退回原字形——这是 HarfBuzz 的标准行为，不需要手动挂 feature）。
   */
  shape(text: string, vertical = false): ShapeRun {
    const buffer = new HbBuffer();
    buffer.addText(text);
    buffer.guessSegmentProperties();
    if (vertical) buffer.setDirection(Direction.TTB);
    shape(this.#font, buffer);
    const raw = buffer.getGlyphInfosAndPositions();
    const glyphs = raw.map((g) => ({
      glyphId: g.codepoint,
      cluster: g.cluster,
      xAdvance: g.xAdvance ?? 0,
      yAdvance: g.yAdvance ?? 0,
      xOffset: g.xOffset ?? 0,
      yOffset: g.yOffset ?? 0,
    }));
    return { glyphs, upem: this.upem };
  }

  /** glyph 的 SVG 轮廓（字体坐标系，y 向上）。渲染侧（M2-18）用。 */
  glyphPath(glyphId: number): string {
    return this.#font.glyphToPath(glyphId);
  }

  /**
   * 本包（harfbuzzjs 的类封装）用 FinalizationRegistry 管理 wasm 侧内存，
   * 没有显式 destroy——保留此方法为将来换底层实现留接口。
   */
  destroy(): void {
    /* GC 托管 */
  }
}
